import type { ConnectionStatus } from "./types";

export interface ActivityMonitorOptions {
  connectionName: string;
  activityTimeoutMs: number;
  logger: (...args: unknown[]) => void;
  /** Returns current connection status — timeout only fires when "active" */
  getStatus: () => ConnectionStatus;
  /** Called when activity timeout occurs — main class decides whether to reconnect or enter polling */
  onTimeout: () => void;
  /**
   * Called when we want to proactively reconnect on visibility return after a
   * long hide — the connection may have silently died during the hide. This
   * is intentional reconnection, NOT an error, so it should not fire Sentry
   * activity-timeout alerts or count against the activity-timeout threshold.
   */
  onForceReconnect: () => void;
}

/**
 * Monitors SSE activity (pings and messages) and fires a timeout callback
 * when no activity is received within the configured threshold.
 */
export class ActivityMonitor {
  private lastActivityTime: number = 0;
  private activityCheckInterval: ReturnType<typeof setInterval> | undefined;
  private isHidden: boolean = false;
  private hiddenSince: number = 0;
  private boundVisibilityHandler: (() => void) | null = null;

  private readonly activityTimeoutMs: number;
  private readonly connectionName: string;
  private readonly logger: (...args: unknown[]) => void;
  private readonly getStatus: () => ConnectionStatus;
  private readonly onTimeout: () => void;
  private readonly onForceReconnect: () => void;

  constructor(options: ActivityMonitorOptions) {
    this.connectionName = options.connectionName;
    this.activityTimeoutMs = options.activityTimeoutMs;
    this.logger = options.logger;
    this.getStatus = options.getStatus;
    this.onTimeout = options.onTimeout;
    this.onForceReconnect = options.onForceReconnect;
  }

  /**
   * Record activity (ping or message received).
   * Starts monitoring if not already running.
   */
  recordActivity(): void {
    this.lastActivityTime = Date.now();

    // Start monitoring if not already running
    if (!this.activityCheckInterval) {
      this.startMonitoring();
    }
  }

  /**
   * Stop activity monitoring.
   */
  stop(): void {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = undefined;
    }
    if (this.boundVisibilityHandler) {
      document.removeEventListener("visibilitychange", this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }
    this.isHidden = false;
    this.hiddenSince = 0;
  }

  /**
   * Full cleanup — stops monitoring.
   */
  destroy(): void {
    this.stop();
  }

  /**
   * Handle visibility change — pause timeout checks when hidden (iOS/mobile
   * aggressively suspends JS timers + network delivery). On return: if the
   * tab was hidden longer than the activity timeout window, the existing
   * connection may have silently died (carrier proxy idle-kill, NAT timeout,
   * etc.) without firing EventSource.onerror, so force a reconnect rather
   * than trust it. Short hides just reset the activity clock.
   */
  private handleVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      this.isHidden = true;
      this.hiddenSince = Date.now();
      this.logger(
        `[${this.connectionName}] Tab hidden, pausing activity monitoring`
      );
    } else {
      const hiddenDuration =
        this.hiddenSince > 0 ? Date.now() - this.hiddenSince : 0;
      this.isHidden = false;
      this.hiddenSince = 0;
      if (hiddenDuration > this.activityTimeoutMs) {
        this.logger(
          `[${this.connectionName}] Tab visible after ${Math.round(hiddenDuration / 1000)}s (>${this.activityTimeoutMs / 1000}s), forcing reconnect`
        );
        this.onForceReconnect();
      } else {
        this.lastActivityTime = Date.now();
        this.logger(
          `[${this.connectionName}] Tab visible after ${Math.round(hiddenDuration / 1000)}s, reset activity timer`
        );
      }
    }
  }

  /**
   * Start periodic checks for activity timeout.
   */
  private startMonitoring(): void {
    // Register visibility listener (once) to avoid false timeouts on
    // iOS / mobile background tabs.
    if (!this.boundVisibilityHandler && typeof document !== "undefined") {
      // Sync the initial state from the current visibilityState. Without
      // this, tabs that load already-hidden (background opens, restored
      // sessions, popouts onto sleeping monitors) start with isHidden=false
      // and fire phantom activity timeouts while the browser is throttling
      // message delivery.
      if (document.visibilityState === "hidden") {
        this.isHidden = true;
        this.hiddenSince = Date.now();
      }
      this.boundVisibilityHandler = () => this.handleVisibilityChange();
      document.addEventListener("visibilitychange", this.boundVisibilityHandler);
    }

    // (more frequent than timeout for responsiveness)
    const checkIntervalMs = Math.max(1000, this.activityTimeoutMs / 2);

    this.activityCheckInterval = setInterval(() => {
      if (this.getStatus() !== "active") {
        return;
      }

      // Skip timeout check while tab is hidden — iOS suspends message
      // delivery but the connection may still be alive on the server.
      if (this.isHidden) {
        return;
      }

      const timeSinceLastActivity = Date.now() - this.lastActivityTime;

      if (timeSinceLastActivity > this.activityTimeoutMs) {
        this.logger(
          `[${this.connectionName}] No activity for ${Math.round(timeSinceLastActivity / 1000)}s, triggering timeout...`
        );
        this.onTimeout();
      }
    }, checkIntervalMs);
  }
}
